import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import { useInventoryStore } from '../../../../stores/appInventoryStore';
import { useCostPriceStore } from '../../../../stores/appCostPriceStore';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../../stores/appUserDefaultStore';
import { useLoadInventoryCountsHistory, useLoadCostPrice } from '../../../../utils/dataLoadingUtils';
import { groupCountsIntoSessions } from '../../inventory/inventoryOverviewUtils';
import { buildConsumptionWindows } from '../../../../utils/reports/consumptionReport';
import { cachedReportValue } from '../../../../utils/reports/reportMemoCache';
import { ReportsGate } from './ReportsGate';
import { ReportLoading } from './common';
import { REPORTS, ReportAvailabilityContext } from './reportsRegistry';
import MobilePageHeader from '../../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../../common/RestaurantSelector';

/**
 * Reports landing page: a responsive grid of report cards driven by the registry.
 * Unavailable reports either hide entirely (inventory-dependent) or show a
 * disabled card with an explainer hint.
 */
const ReportsHubPage: React.FC = () => {
  const { t } = useTranslation();

  // Inventory counts gate the availability of the inventory + consumption reports;
  // cost prices gate utilization + cost price & margin.
  useLoadInventoryCountsHistory();
  useLoadCostPrice();
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const isAllRestaurants = !!selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const countsHistory = useInventoryStore(s => s.countsHistory);
  const isLoadingCounts = useInventoryStore(s => s.isLoadingCounts);
  const costPriceList = useCostPriceStore(s => s.costPriceList);
  const isLoadingCostPrice = useCostPriceStore(s => s.isLoading);
  // Cached across mounts (and shared with the consumption report for the windows
  // build) so navigating hub ↔ report doesn't recompute these each time.
  const sessions = cachedReportValue('hub.sessions', [countsHistory], () =>
    groupCountsIntoSessions(countsHistory)
  );
  // R7 needs ≥2 counts within a single group or section, not just ≥2 sessions overall.
  const hasConsumptionWindow = cachedReportValue('consumption.windows', [countsHistory], () =>
    buildConsumptionWindows(countsHistory)
  ).hasAnyWindow;

  // While availability data is still loading and empty, show a spinner instead of
  // hiding cards / flashing unavailable hints that may flip once data arrives.
  const isLoadingAvailability =
    !isAllRestaurants && ((isLoadingCounts && countsHistory.length === 0) ||
    (isLoadingCostPrice && costPriceList.length === 0));

  const ctx: ReportAvailabilityContext = {
    inventorySessionsCount: sessions.length,
    hasConsumptionWindow,
    costPriceList,
  };

  return (
    <ReportsGate>
      <MobilePageHeader title={t('reports.hub.title')} right={<RestaurantSelector />} />
      <div className="p-4 max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <TrendingUp className="hidden lg:block w-8 h-8 text-textGreen dark:text-textGreen-dark" />
          <h1 className="hidden lg:block text-3xl font-bold text-textPrimary dark:text-textPrimary-dark">{t('reports.hub.title')}</h1>
        </div>
        <p className="text-muted dark:text-muted-dark mb-6">{t('reports.hub.subtitle')}</p>

        {isAllRestaurants && (
          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
            <p className="text-blue-800 dark:text-blue-200 text-sm">{t('reports.hub.allRestaurantsNotice')}</p>
          </div>
        )}

        {isLoadingAvailability ? (
          <ReportLoading />
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {REPORTS.map((report) => {
            const Icon = report.icon;

            // In "all restaurants" mode, reports that can't aggregate cleanly across
            // restaurants show a disabled card asking to pick a single restaurant
            // (regardless of their single-restaurant availability data, which isn't
            // even loaded in this mode).
            if (isAllRestaurants && !report.supportsAllRestaurants) {
              return (
                <div
                  key={report.key}
                  className="bg-background dark:bg-background-dark border border-border dark:border-border-dark rounded-xl shadow-sm p-5 opacity-60"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className="w-6 h-6 text-muted dark:text-muted-dark" />
                    <h2 className="text-lg font-semibold text-textPrimary dark:text-textPrimary-dark">{t(report.titleKey)}</h2>
                  </div>
                  <p className="text-sm text-muted dark:text-muted-dark">{t('reports.hub.singleRestaurantOnly')}</p>
                </div>
              );
            }

            const available = (isAllRestaurants && report.supportsAllRestaurants) || report.isAvailable(ctx);

            if (!available && report.hideWhenUnavailable) return null;

            if (!available) {
              return (
                <div
                  key={report.key}
                  className="bg-background dark:bg-background-dark border border-border dark:border-border-dark rounded-xl shadow-sm p-5 opacity-60"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className="w-6 h-6 text-muted dark:text-muted-dark" />
                    <h2 className="text-lg font-semibold text-textPrimary dark:text-textPrimary-dark">{t(report.titleKey)}</h2>
                  </div>
                  <p className="text-sm text-muted dark:text-muted-dark">
                    {report.unavailableHintKey ? t(report.unavailableHintKey) : t(report.descriptionKey)}
                  </p>
                </div>
              );
            }

            return (
              <Link
                key={report.key}
                to={`/reports/${report.key}`}
                className="bg-background dark:bg-background-dark border border-border dark:border-border-dark rounded-xl shadow-sm p-5 hover:shadow-md transition"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Icon className="w-6 h-6 text-textGreen dark:text-textGreen-dark" />
                  <h2 className="text-lg font-semibold text-textPrimary dark:text-textPrimary-dark">
                    {t(report.titleKey)}
                  </h2>
                </div>
                <p className="text-sm text-muted dark:text-muted-dark">{t(report.descriptionKey)}</p>
              </Link>
            );
          })}
        </div>
        )}
      </div>
    </ReportsGate>
  );
};

export default ReportsHubPage;
